import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import { DateRange } from "../../../components/ui/daterangeselect/DateRangeSelect";
import { selectDashboardRaw } from "../logic/dashboardSlice";
import { fetchDashboardMetricsThunk } from "../logic/dashboardThunk";
import Header from "./Header";
import CasesAnalyticsOverview from "./casesAnalyticsOverview";
import IncidentsByBranchChart from "./charts/IncidentsByBranchCharts";
import IncidentsByFraudTypeChart from "./charts/IncidentsByFraudTypeChart";
import IncidentsByMonthChart from "./charts/IncidentsByMonthChart";
import IncidentsByNatureChart from "./charts/IncidentsByNatureChart";
import IncidentsByZoneChart from "./charts/IncidentsByZoneChart";
import PageTitle from "../../pageheader/components/PageTitle";

const DashboardPage: React.FC = () => {
  const dispatch = useAppDispatch();

    const latestRequestIdRef = useRef<string | null>(null);

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const dashboardRaw = useAppSelector(selectDashboardRaw);
  const overview = dashboardRaw?.analytics_overview;

  useEffect(() => {
    document.body.style.overflow = "auto";
  }, []);

  useEffect(() => {
    if (!startDate || !endDate) return;
    setIsLoading(true);
    setError(undefined);

    const action = dispatch(
      fetchDashboardMetricsThunk({
        params: { start_date: startDate, end_date: endDate },
        force: true,
      }),
    );
    latestRequestIdRef.current = action.requestId;

    action
      .unwrap()
      .then(() => {
        if (latestRequestIdRef.current !== action.requestId) return;
        setError(undefined);
      })
      .catch((e) => {
        if (latestRequestIdRef.current !== action.requestId) return;
        setError(
          typeof e === "string"
            ? e
            : (e as any)?.message ?? "Failed to fetch dashboard metrics",
        );
      })
      .finally(() => {
        if (latestRequestIdRef.current !== action.requestId) return;
        setIsLoading(false);
      });
  }, [dispatch, endDate, startDate]);

  const renderCharts = () => (
    <div className="space-y-6">
      <IncidentsByMonthChart isLoading={isLoading} error={error} />
      <IncidentsByZoneChart isLoading={isLoading} error={error} />
      <IncidentsByBranchChart isLoading={isLoading} error={error} />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <IncidentsByFraudTypeChart isLoading={isLoading} error={error} />
        <IncidentsByNatureChart isLoading={isLoading} error={error} />
      </div>
    </div>
  );

  return (
    <div className="flex-1 bg-gray-50 overflow-auto">
      <div className="flex-1 p-6">
        <PageTitle title="Dashboard" />
                
        <div className="mx-auto w-full max-w-7xl space-y-6">
          <Header
            onDateRangeChange={(range: DateRange) => {
              setStartDate(range.from);
              setEndDate(range.to);
            }}
            // onRefresh={() => {
            //   if (!startDate || !endDate) return;
            //   dispatch(
            //     fetchDashboardMetricsThunk({
            //       params: { start_date: startDate, end_date: endDate },
            //       force: true,
            //     }),
            //   );
            // }}
          />
          <CasesAnalyticsOverview
            casesOpened={overview?.cases_opened}
            fraudAmount={overview?.fraud_amount}
            declaredFraud={overview?.cases_declared_fraud}
            declaredNoFraud={overview?.cases_declared_no_fraud}
            casesInProgress={overview?.cases_in_progress}
            casesOpen={overview?.cases_open}
            casesDeactivated={overview?.cases_deactivated}
            casesClosed={overview?.cases_closed}
            isLoading={isLoading}
          />
          {renderCharts()}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
